/**
 * oddit-video-embed.js
 * Shared video embed utility used by multiple sections.
 *
 * Exposes window.OdditVideoEmbed.createPlayer(videoUrl, videoType, options)
 * which returns a <video> or <iframe> element ready to append to the DOM,
 * or null when the URL cannot be resolved to an embed.
 */
(function () {
  'use strict';

  var YT_RE = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?]+)/;
  var VIMEO_RE = /vimeo\.com\/(\d+)/;

  var IFRAME_ALLOW =
    'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';

  /**
   * Build a YouTube or Vimeo embed URL from a page URL.
   * Returns '' if the URL doesn't match either platform.
   */
  function buildEmbedUrl(url, muted) {
    var muteFlag = muted === false ? 0 : 1;
    var ytMatch = url.match(YT_RE);
    if (ytMatch) {
      return 'https://www.youtube.com/embed/' + ytMatch[1] + '?autoplay=1&mute=' + muteFlag + '&rel=0';
    }
    var vimeoMatch = url.match(VIMEO_RE);
    if (vimeoMatch) {
      return 'https://player.vimeo.com/video/' + vimeoMatch[1] + '?autoplay=1&muted=' + muteFlag;
    }
    return '';
  }

  /**
   * Create a ready-to-append player element.
   *
   * @param {string} videoUrl  - Hosted file URL or YouTube/Vimeo page URL
   * @param {string} videoType - 'hosted' | 'youtube' | 'vimeo' (anything
   *                              other than 'hosted' triggers iframe path)
   * @param {Object} [options]
   * @param {string} [options.videoStyle]  - cssText applied to <video>
   * @param {string} [options.iframeStyle] - cssText applied to <iframe>
   * @param {string} [options.controls] - 'controls' | ''
   * @param {string} [options.loop] - 'loop' | ''
   * @param {boolean} [options.muted] - start muted (default true). When false,
   *                                    attempts unmuted playback (relies on
   *                                    user-gesture context) and falls back to
   *                                    muted if the browser blocks audio.
   * @returns {HTMLVideoElement|HTMLIFrameElement|null}
   */
  function createPlayer(videoUrl, videoType, options) {
    var opts = options || {};
    var startMuted = opts.muted !== false;

    if (videoType === 'hosted') {
      var video = document.createElement('video');
      video.setAttribute('src', videoUrl);
      video.setAttribute('autoplay', '');
      video.setAttribute('playsinline', '');
      if (options.controls != 'false') {
        video.setAttribute('controls', '');
      }
      if (options.loop != 'false') {
        video.setAttribute('loop', '');
      }
      video.muted = startMuted;
      if (opts.videoStyle) video.style.cssText = opts.videoStyle;
      var playPromise = video.play();
      if (playPromise && playPromise.catch) {
        playPromise.catch(function () {
          if (!video.muted) {
            video.muted = true;
            video.play().catch(function () {});
          }
        });
      }
      return video;
    }

    var embedUrl = buildEmbedUrl(videoUrl, startMuted);
    if (!embedUrl) return null;

    var iframe = document.createElement('iframe');
    iframe.setAttribute('src', embedUrl);
    iframe.setAttribute('allow', IFRAME_ALLOW);
    iframe.setAttribute('allowfullscreen', '');
    if (opts.iframeStyle) iframe.style.cssText = opts.iframeStyle;
    return iframe;
  }

  window.OdditVideoEmbed = {
    createPlayer: createPlayer,
    buildEmbedUrl: buildEmbedUrl,
  };
})();
